import { AI_PLANNER_BRIDGE_INSTANCE_PARAM } from '@atlassian/ai-planner-embed-bridge/AiPlannerBridgeCommands';
import { PLANNER_BASE_PATH } from './plannerBasePath.constants.tsx';

/**
 * Tenant context Planner needs to boot quickly in embed mode.
 *
 * Per the planner's embed-mode contract, these are passed as hints for
 * telemetry / feature-gate targeting and to skip a tenant lookup — they are
 * never used for authorization (all planner data calls remain auth-scoped).
 */
export type AiPlannerEmbedContext = {
	cloudId: string;
	/** Null for anonymous users / before tenant context initializes. */
	accountId: string | null;
};

/**
 * Jira's current color mode, as read from Jira's own theme state (e.g.
 * `useThemeObserver` / the `data-color-mode` attribute) — NEVER the OS/browser
 * `prefers-color-scheme` preference. Jira and the OS can disagree (e.g. a user
 * pins Jira to dark while their OS is light), so the planner must match Jira's
 * chrome, not the device.
 */
export type AiPlannerColorMode = 'light' | 'dark';

type PlannerSearchParamsInput = string | URLSearchParams;

const HOST_OWNED_PLANNER_PARAMS = [
	'embed',
	'cloudId',
	'accountId',
	'colorMode',
	'bifrost-override',
	'ai-planner-bifrost-override',
	AI_PLANNER_BRIDGE_INSTANCE_PARAM,
];

export const getAiPlannerUrl = (
	embedContext: AiPlannerEmbedContext,
	planId?: string,
	// When set, forwarded to the planner as `bifrost-override` so a specific
	// planner Bifrost build can be pinned (sourced from the Jira page URL's
	// `ai-planner-bifrost-override` query param via getBifrostOverrideFromLocation).
	bifrostOverride?: string,
	plannerSearch?: PlannerSearchParamsInput,
	// Per-iframe bridge instance id. When set, forwarded so the planner derives a
	// private Rovo bridge channel for this embed; when absent the planner falls
	// back to the global bridge ids.
	bridgeInstanceId?: string,
	// Jira's current color mode, sourced from Jira's theme state (see
	// `AiPlannerColorMode`) — never the OS preference. Defaults to 'light' for
	// callers that haven't resolved a theme yet (e.g. very first paint).
	colorMode: AiPlannerColorMode = 'light',
): string => {
	const path =
		planId != null && planId !== ''
			? `${PLANNER_BASE_PATH}/plan/${encodeURIComponent(planId)}`
			: PLANNER_BASE_PATH;

	const params = new URLSearchParams(plannerSearch);
	for (const param of HOST_OWNED_PLANNER_PARAMS) {
		params.delete(param);
	}

	params.set('embed', 'true');
	params.set('cloudId', embedContext.cloudId);
	if (embedContext.accountId != null && embedContext.accountId !== '') {
		params.set('accountId', embedContext.accountId);
	}
	params.set('colorMode', colorMode);
	if (bifrostOverride != null && bifrostOverride !== '') {
		params.set('bifrost-override', bifrostOverride);
	}
	if (bridgeInstanceId != null && bridgeInstanceId !== '') {
		params.set(AI_PLANNER_BRIDGE_INSTANCE_PARAM, bridgeInstanceId);
	}

	return `${path}?${params.toString()}`;
};
